import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DonationSection = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const predefinedAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonation = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    try {
      // Validate amount
      const { donationSchema } = await import('@/lib/validations');
      donationSchema.parse({ amount });
    } catch (error: any) {
      toast({
        title: "Valor inválido",
        description: error?.issues?.[0]?.message || "Por favor, selecione ou insira um valor válido para doar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Create Stripe payment link using Supabase edge function
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: Math.round(amount * 100) // Convert to cents
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        // Open Stripe payment page in new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('Não foi possível criar o link de pagamento');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Erro ao processar doação",
        description: "Não foi possível processar sua doação. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="donation-section" className="py-20 bg-gradient-primary relative overflow-hidden">
      <div className="absolute inset-0 bg-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            A sua doação 
            <span className="text-accent-orange"> salva vidas</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Cada doação aproxima-nos um passo mais de encontrar curas para as doenças neurológicas. 
            O seu apoio torna a nossa investigação possível.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-strong border-0 backdrop-blur-sm bg-card/95 dark:bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-foreground">
                Fazer uma Doação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Selecione um valor:
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 ${
                        selectedAmount === amount 
                          ? "bg-primary hover:bg-primary-dark text-white shadow-medium" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      €{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Ou insira um valor personalizado:
                </label>
                <Input
                  type="number"
                  placeholder="Valor em EUR"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="text-lg py-3"
                />
              </div>

              <div className="bg-accent p-4 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Com <span className="font-semibold text-foreground">
                    €{customAmount || selectedAmount}
                  </span> podemos financiar{" "}
                  {customAmount || selectedAmount >= 250 
                    ? "uma semana completa de investigação em laboratório" 
                    : customAmount || selectedAmount >= 100
                    ? "análises avançadas de amostras neurológicas"
                    : "materiais essenciais para as nossas experiências"
                  }
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-secondary hover:bg-secondary-light text-white py-4 text-lg font-semibold shadow-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDonation}
                disabled={isLoading}
              >
                {isLoading ? "A processar..." : `Doar €${customAmount || selectedAmount}`}
              </Button>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left space-y-4">
                <h4 className="font-semibold text-foreground text-sm">Descubra todas as vantagens de doar à Fundação NeuroResearch</h4>
                
                <p className="text-xs text-muted-foreground">
                  Seja pessoa física ou jurídica, as suas doações à Fundação NeuroResearch podem ser deduzidas do imposto sobre o rendimento ou do imposto sobre as sociedades apenas em Espanha. A nossa organização está abrangida pela Lei 49/2002, sobre o regime fiscal das entidades sem fins lucrativos e incentivos ao mecenato. Além disso, o Real Decreto-Lei 6/2023 melhorou as deduções para doações a ONGs, pelo que agora colaborar tem ainda mais vantagens.
                </p>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deduções para pessoas físicas (IRS)</h5>
                  <p className="text-xs text-muted-foreground mb-2">A percentagem que pode deduzir depende do montante e de se doa de forma contínua:</p>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Até 250 € por ano → dedução de 80%.</li>
                    <li>Montante superior a 250 € → dedução de 40%.</li>
                    <li>Se doar durante 3 anos ou mais e mantiver ou aumentar a sua contribuição → a percentagem sobre a parte que excede os 250 € sobe para 45%.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Exemplos práticos:</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Se doar 250 €, as Finanças devolvem-lhe 200 €, portanto o seu custo real será de 50 €.</li>
                    <li>Se doar 400 €: Primeiros dois anos → dedução de 260 €.</li>
                    <li>A partir do terceiro ano → dedução de 267,5 €.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    Lembre-se: Se a sua comunidade autónoma tiver deduções adicionais ou regimes regionais próprios, o benefício fiscal pode ser ainda maior.
                  </p>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deduções para empresas (IRC)</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>40% de dedução geral.</li>
                    <li>50% se a doação for recorrente (três anos ou mais).</li>
                    <li>Limite: 15% da base tributável.</li>
                    <li>Se ultrapassar o limite, o excesso pode ser aplicado nos 10 anos seguintes.</li>
                  </ul>
                </div>

                <p className="text-xs text-primary font-medium">
                  Se quiser apoiar-nos, entre em contacto.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 text-white text-center">
          <div>
            <div className="text-3xl font-bold mb-2">€2.5M</div>
            <div className="text-white/80">Angariado em 2025</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">1,200+</div>
            <div className="text-white/80">Doadores ativos</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">95%</div>
            <div className="text-white/80">Vai direto para investigação</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;
